import { useState } from 'react';
import { 
  User, Calendar, CreditCard, Clock, AlertCircle, 
  ChevronRight, X, RefreshCw, Ban, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MOCK_USER, formatDate, getWhatsAppLink } from '../data/mockData';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export const Account = () => {
  const [activeTab, setActiveTab] = useState('reservas');
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(null);

  const user = MOCK_USER;

  const handleCancelBooking = (bookingId) => {
    toast.success('Reserva cancelada', {
      description: 'Tu lugar ha sido liberado.',
    });
    setShowCancelModal(null);
  };

  const handleReschedule = (bookingId) => {
    toast.success('Clase reprogramada', {
      description: 'Te confirmamos tu nuevo horario.',
    });
    setShowRescheduleModal(null);
  };

  const handleLeaveWaitlist = (waitlistId) => {
    toast.success('Saliste de la lista de espera');
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-24 md:pb-8 bg-slate-50" data-testid="account-page">
      <div className="container-app">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-soft mb-6" data-testid="profile-header">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-[#16263B] rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="font-heading font-bold text-2xl text-white">
                {user.name[0]}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="font-heading font-bold text-2xl text-slate-900">
                Hola, {user.name}
              </h1>
              <p className="text-slate-500">{user.email}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="flex items-center gap-1.5 text-sm text-slate-600">
                  <CreditCard className="w-4 h-4 text-[#16263B]" />
                  Plan {user.membership}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Calendar className="w-4 h-4 text-[#16263B]" />
                  Miembro desde {new Date(user.memberSince).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div className="text-center">
              <p className="font-heading font-bold text-2xl text-slate-900">{user.credits}</p>
              <p className="text-xs text-slate-500">Créditos disponibles</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-2xl text-slate-900">{user.upcomingClasses.length}</p>
              <p className="text-xs text-slate-500">Próximas clases</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-2xl text-slate-900">{user.pastClasses}</p>
              <p className="text-xs text-slate-500">Clases tomadas</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
          {[
            { id: 'reservas', label: 'Mis reservas', icon: Calendar },
            { id: 'waitlist', label: 'Lista de espera', icon: Users },
            { id: 'cuenta', label: 'Mi cuenta', icon: User },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-[#16263B] text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'reservas' && (
          <div className="space-y-4" data-testid="reservas-tab">
            <h2 className="font-heading font-semibold text-lg text-slate-900">
              Próximas clases
            </h2>
            
            {user.upcomingClasses.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No tienes clases reservadas</p>
                <Button className="btn-primary">
                  Explorar horarios
                </Button>
              </div>
            ) : (
              user.upcomingClasses.map((booking) => (
                <div
                  key={booking.id}
                  className="account-card"
                  data-testid={`booking-${booking.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#16263B]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-[#16263B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">{booking.className}</h3>
                      <p className="text-sm text-slate-500">{booking.coach}</p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                        <span className="capitalize">{formatDate(booking.date)}</span>
                        <span>{booking.time}</span>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                      Confirmada
                    </span>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-full"
                      onClick={() => setShowRescheduleModal(booking)}
                      data-testid={`reschedule-${booking.id}`}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Reprogramar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setShowCancelModal(booking)}
                      data-testid={`cancel-${booking.id}`}
                    >
                      <Ban className="w-4 h-4 mr-1" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'waitlist' && (
          <div className="space-y-4" data-testid="waitlist-tab">
            <h2 className="font-heading font-semibold text-lg text-slate-900">
              Lista de espera
            </h2>
            
            {user.waitlist.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No estás en ninguna lista de espera</p>
              </div>
            ) : (
              user.waitlist.map((item) => (
                <div
                  key={item.id}
                  className="account-card"
                  data-testid={`waitlist-${item.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900">{item.className}</h3>
                      <p className="text-sm text-slate-500">{item.coach}</p>
                      <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                        <span className="capitalize">{formatDate(item.date)}</span>
                        <span>{item.time}</span>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
                      #{item.position} en espera
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Te notificaremos si se libera un lugar
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleLeaveWaitlist(item.id)}
                      data-testid={`leave-waitlist-${item.id}`}
                    >
                      Salir
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'cuenta' && (
          <div className="space-y-4" data-testid="cuenta-tab">
            <div className="account-card">
              <h3 className="font-semibold text-slate-900 mb-4">Información personal</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Nombre</span>
                  <span className="text-slate-900">{user.name} {user.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Email</span>
                  <span className="text-slate-900">{user.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Teléfono</span>
                  <span className="text-slate-900">{user.phone}</span>
                </div>
              </div>
            </div>

            <div className="account-card">
              <h3 className="font-semibold text-slate-900 mb-4">Membresía</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan actual</span>
                  <span className="text-slate-900">{user.membership}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Próxima renovación</span>
                  <span className="text-slate-900">{new Date(user.nextRenewal).toLocaleDateString('es-MX')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Clases tomadas</span>
                  <span className="text-slate-900">{user.pastClasses}</span>
                </div>
              </div>
            </div>

            <div className="account-card">
              <h3 className="font-semibold text-slate-900 mb-4">Acciones</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-between rounded-xl">
                  Cambiar plan
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" className="w-full justify-between rounded-xl">
                  Pausar membresía
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <a
                  href={getWhatsAppLink('Hola, necesito ayuda con mi cuenta')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="w-full justify-between rounded-xl">
                    Soporte por WhatsApp
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      <Dialog open={!!showCancelModal} onOpenChange={(open) => !open && setShowCancelModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Cancelar reserva?</DialogTitle>
          </DialogHeader>
          {showCancelModal && (
            <div className="py-4">
              <p className="text-slate-500 mb-4">
                Estás por cancelar tu clase de <strong>{showCancelModal.className}</strong> el {formatDate(showCancelModal.date)} a las {showCancelModal.time}.
              </p>
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl mb-4">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Cancelaciones con menos de 2 horas de anticipación pueden afectar tu cuenta.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setShowCancelModal(null)}
                >
                  No, mantener
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full"
                  onClick={() => handleCancelBooking(showCancelModal.id)}
                  data-testid="confirm-cancel-btn"
                >
                  Sí, cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={!!showRescheduleModal} onOpenChange={(open) => !open && setShowRescheduleModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reprogramar clase</DialogTitle>
          </DialogHeader>
          {showRescheduleModal && (
            <div className="py-4">
              <p className="text-slate-500 mb-4">
                Selecciona un nuevo horario para <strong>{showRescheduleModal.className}</strong>.
              </p>
              {/* Mock time slots */}
              <div className="space-y-2 mb-4">
                {['10:00', '12:00', '18:30'].map((time) => (
                  <button
                    key={time}
                    className="w-full p-3 bg-slate-50 hover:bg-[#16263B]/20 rounded-xl text-left transition-colors"
                    onClick={() => handleReschedule(showRescheduleModal.id)}
                  >
                    <span className="font-medium text-slate-900">{time}</span>
                    <span className="text-sm text-slate-500 ml-2">• Mañana</span>
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => setShowRescheduleModal(null)}
              >
                Cancelar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
